import { useState, useEffect, useCallback, useRef } from "react";
import { Form, Formik, useFormikContext } from "formik";
import { useMutation } from "@tanstack/react-query";
import { toast } from "react-toastify";
import lodash from "lodash";

import { getNextDate } from "../../util/dateHelpers";
import { enums } from "../../constants/enum";
import { deepMerge } from "../../util/jsObjects";
import currencyFormatter from "../../util/currencyFormatter";

import { DownloadIcon } from "../../assets/icons";
import { Button, PageTitle, Section, Tab } from "../../components";
import {
  Checkbox,
  DatePicker,
  EyeMeasurementInput,
  SelectInput,
  TextInput,
} from "../../components/forms";

import NewCustomerTab from "./NewCustomerTab";
import ExistingCustomersTab from "./ExistingCustomersTab";
import SelectedCustomer from "./SelectedCustomer";

import "./PrescriptionFormPage.scss";
import useAuthentication from "../../hooks/auth";
import useOrderFormService from "../../services/api/orderFormService";

const initialValues = {
  customer: "",
  eyeCheckingNumber: "",
  hb: "",
  CR39: false,
  PHOTO_GREY: false,
  MULTI_COATED: false,
  PROGRESSIVE: false,
  EASY_FIT: false,
  distance: {
    right: { sph: "", cyl: "", axis: 0 },
    left: { sph: "", cyl: "", axis: 0 },
  },
  near: {
    right: { sph: "", cyl: "", axis: 0 },
    left: { sph: "", cyl: "", axis: 0 },
  },
  frame: {
    brand: "",
    model_number: "",
    lens_diagonal: "",
    bridge: "",
    arm_length: "",
    color: "",
    pd: "",
    sellingPrice: 0,
  },
  lense: {
    left: {
      type: "",
      diameter: "",
      coating: "",
      seg: "",
    },
    right: {
      type: "",
      diameter: "",
      coating: "",
      seg: "",
    },
    sellingPrice: 0,
  },
  testDate: "",
};

function OldPrescriptionFormPage() {
  const { profile } = useAuthentication();
  const { createArchivedOrderForm } = useOrderFormService();
  const [activeCustomerTab, setActiveCustomerTab] =
    useState("Existing Customers");
  const [formData, setFormData] = useState(initialValues);

  const handleGeneralUpdate = useCallback((values) => {
    setFormData((prev) => deepMerge(prev, values));
  }, []);

  const handlePriceUpdate = useCallback((data) => {
    setFormData((prev) => {
      const framePrice = data.framePrice === "" ? 0 : Number(data.framePrice);
      const lensePrice = data.lensePrice === "" ? 0 : Number(data.lensePrice);

      if (
        prev.frame.sellingPrice === framePrice &&
        prev.lense.sellingPrice === lensePrice
      ) {
        return prev;
      }
      return {
        ...prev,
        frame: {
          ...prev.frame,
          sellingPrice: framePrice,
        },
        lense: {
          ...prev.lense,
          sellingPrice: lensePrice,
        },
      };
    });
  }, []);

  const handleCustomerSelect = useCallback((id) => {
    setFormData((prev) => {
      const customerId = typeof id === "function" ? id({ customer: prev.customer }).customer : id;
      if (prev.customer === customerId) return prev;
      return { ...prev, customer: customerId };
    });
  }, []);

  const createArchivedOrderFormMutation = useMutation({
    mutationFn: createArchivedOrderForm,
    onSuccess: (payload) => {
      toast.success(payload?.message || "Prescription added successfully");
      setFormData(initialValues);
    },
    onError: (error) => {
      toast.error(
        error?.response?.data?.payload?.message || enums.DEFAULT_ERROR_MESSAGE,
      );
    },
  });

  const handleSubmit = () => {
    if (!formData.customer) {
      toast.error("Please select a customer");
      return;
    }

    const formatMeasurement = (val) => (val === "" || val === null ? null : String(val));
    const formatNumber = (val) => (val === "" || val === null ? 0 : Number(val));

    const payload = {
      prescription: {
        customer: formData.customer,
        eyeCheckingNumber: formData.eyeCheckingNumber,
        testDate: formData.testDate,
        hb: String(formData.hb || ""),
        CR39: !!formData.CR39,
        PHOTO_GREY: !!formData.PHOTO_GREY,
        MULTI_COATED: !!formData.MULTI_COATED,
        PROGRESSIVE: !!formData.PROGRESSIVE,
        EASY_FIT: !!formData.EASY_FIT,
        distance: {
          right: {
            sph: formatMeasurement(formData.distance.right.sph),
            cyl: formatMeasurement(formData.distance.right.cyl),
            axis: formatMeasurement(formData.distance.right.axis),
          },
          left: {
            sph: formatMeasurement(formData.distance.left.sph),
            cyl: formatMeasurement(formData.distance.left.cyl),
            axis: formatMeasurement(formData.distance.left.axis),
          },
        },
        near: {
          right: {
            sph: formatMeasurement(formData.near.right.sph),
            cyl: formatMeasurement(formData.near.right.cyl),
            axis: formatMeasurement(formData.near.right.axis),
          },
          left: {
            sph: formatMeasurement(formData.near.left.sph),
            cyl: formatMeasurement(formData.near.left.cyl),
            axis: formatMeasurement(formData.near.left.axis),
          },
        },
      },
      items: {
        leftLense: {
          attributes: {
            power_range: {
              sph: formatMeasurement(formData.distance.left.sph),
              cyl: formatMeasurement(formData.distance.left.cyl),
              add: "0",
            },
            lense_type: formData.lense.left.type || "SV",
            coating: formData.lense.left.coating || "UNCOATED",
            seg: String(formData.lense.left.seg || "0"),
            diameter: formatMeasurement(formData.lense.left.diameter),
          },
          sellingPrice: formatNumber(formData.lense.sellingPrice),
        },
        rightLense: {
          attributes: {
            power_range: {
              sph: formatMeasurement(formData.distance.right.sph),
              cyl: formatMeasurement(formData.distance.right.cyl),
              add: "0",
            },
            lense_type: formData.lense.right.type || "SV",
            coating: formData.lense.right.coating || "UNCOATED",
            seg: String(formData.lense.right.seg || "0"),
            diameter: formatMeasurement(formData.lense.right.diameter),
          },
          sellingPrice: formatNumber(formData.lense.sellingPrice),
        },
        frame: {
          brand: formData.frame.brand || "",
          attributes: {
            model_number: formData.frame.model_number || "",
            color: formData.frame.color || "",
            size: {
              lens_diagonal: formatNumber(formData.frame.lens_diagonal),
              bridge: formatNumber(formData.frame.bridge),
              arm_length: formatNumber(formData.frame.arm_length),
              pd: formatNumber(formData.frame.pd),
            },
          },
          sellingPrice: formatNumber(formData.frame.sellingPrice),
        },
      },
    };
    // console.log("Submitting payload:", payload);
    createArchivedOrderFormMutation.mutate({ payload });
  };

  return (
    <div className="page prescription-form-page">
      <PageTitle text="Old Prescription Form" />

      <Section
        title="Prescription Details"
        className="prescription-details-section"
      >
        <Formik
          initialValues={{
            eyeCheckingNumber: formData.eyeCheckingNumber,
            testDate: formData.testDate,
          }}
          enableReinitialize
          onSubmit={() => {}}
        >
          <Form>
            <div className="input-row">
              <TextInput
                name="eyeCheckingNumber"
                label="Prescription Number"
                placeholder="Type here"
              />
            </div>

            <div className="input-row">
              <div className="date-pic-input">
                <span>Date</span>
                <DatePicker
                  name="testDate"
                  label="Date"
                  max={getNextDate(0).toISOString().split("T")[0]}
                />
              </div>

              <div>
                <span>Created By</span>
                <p>
                  <span>{profile?.role}</span> {profile?.fullName}
                </p>
              </div>
            </div>
            <AutoUpdateFormData onUpdate={handleGeneralUpdate} />
          </Form>
        </Formik>
      </Section>

      {formData.customer ? (
        <SelectedCustomer
          setSelectedCustomerId={handleCustomerSelect}
          customerId={formData.customer}
        />
      ) : (
        <Section title="Customer Details" className="customer-details-section">
          <Tab
            tabs={["Existing Customers", "New Customer"]}
            activeTab={activeCustomerTab}
            onTabChange={setActiveCustomerTab}
            className="customer-details-tab"
          >
            {activeCustomerTab === "Existing Customers" && (
              <ExistingCustomersTab onSelect={handleCustomerSelect} />
            )}
            {activeCustomerTab === "New Customer" && (
              <NewCustomerTab onSelect={handleCustomerSelect} />
            )}
          </Tab>
        </Section>
      )}

      <Formik
        initialValues={{
          hb: formData.hb,
          CR39: formData.CR39,
          PHOTO_GREY: formData.PHOTO_GREY,
          MULTI_COATED: formData.MULTI_COATED,
          PROGRESSIVE: formData.PROGRESSIVE,
          EASY_FIT: formData.EASY_FIT,
          distance: formData.distance,
          near: formData.near,
        }}
        enableReinitialize
        onSubmit={() => {}}
      >
        <Form>
          <Section
            title="Eye Measurements"
            className="eye-measurements-section"
          >
            <div className="input-group">
              <TextInput
                label="Half Bifocal (H.B)"
                placeholder="Type here"
                name="hb"
                prefix="+"
              />
              <div className="lens-options">
                <span>Lens options</span>
                <div>
                  <div className="checkbox-group">
                    <Checkbox name="CR39" label="CR39" />
                    <Checkbox name="PHOTO_GREY" label="Photo Grey" />
                    <Checkbox name="MULTI_COATED" label="Multi Coated" />
                  </div>
                  <div className="checkbox-group">
                    <Checkbox name="PROGRESSIVE" label="Progressive" />
                    <Checkbox name="EASY_FIT" label="Easy Fit" />
                  </div>
                </div>
              </div>
            </div>

            <div className="table-group">
              <div className="table-content">
                <table>
                  <thead>
                    <tr>
                      <th>R</th>
                      <th>SPH</th>
                      <th>CYL</th>
                      <th>AXIS</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>DIST</td>
                      <td>
                        <EyeMeasurementInput name="distance.right.sph" />
                      </td>
                      <td>
                        <EyeMeasurementInput name="distance.right.cyl" />
                      </td>
                      <td>
                        <TextInput
                          className="tinput"
                          name="distance.right.axis"
                        />
                      </td>
                    </tr>
                    <tr>
                      <td>NEAR</td>
                      <td>
                        <EyeMeasurementInput name="near.right.sph" />
                      </td>
                      <td>
                        <EyeMeasurementInput name="near.right.cyl" />
                      </td>
                      <td>
                        <TextInput className="tinput" name="near.right.axis" />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="table-content">
                <table>
                  <thead>
                    <tr>
                      <th>L</th>
                      <th>SPH</th>
                      <th>CYL</th>
                      <th>AXIS</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>DIST</td>
                      <td>
                        <EyeMeasurementInput name="distance.left.sph" />
                      </td>
                      <td>
                        <EyeMeasurementInput name="distance.left.cyl" />
                      </td>
                      <td>
                        <TextInput
                          className="tinput"
                          name="distance.left.axis"
                        />
                      </td>
                    </tr>
                    <tr>
                      <td>NEAR</td>
                      <td>
                        <EyeMeasurementInput name="near.left.sph" />
                      </td>
                      <td>
                        <EyeMeasurementInput name="near.left.cyl" />
                      </td>
                      <td>
                        <TextInput className="tinput" name="near.left.axis" />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </Section>
          <AutoUpdateFormData onUpdate={handleGeneralUpdate} />
        </Form>
      </Formik>
      <Formik
        initialValues={{ lense: formData.lense }}
        enableReinitialize
        onSubmit={() => {}}
      >
        <Form>
          <Section title="Lens Details" className="lens-details-section">
            <div className="right-lens">
              <h3>Right Lens</h3>
              <div className="lens-table">
                <div className="table-container">
                  <table>
                    <tbody>
                      <tr>
                        <td rowSpan={5} className="table-title">
                          <p>LENS</p>
                        </td>
                      </tr>

                      <tr>
                        <td className="table-sub-title">Type</td>
                        <td>
                          <SelectInput
                            name="lense.right.type"
                            label=""
                            className="prescription-input"
                            values={[
                              { label: "Select Lense Type", value: "" },
                              ...Object.values(enums.LENSE_TYPES).map((t) => ({
                                label: t,
                                value: t,
                              })),
                            ]}
                          />
                        </td>
                      </tr>
                      <tr>
                        <td className="table-sub-title">Diameter</td>
                        <td>
                          <TextInput
                            className="prescription-input"
                            name="lense.right.diameter"
                            placeholder="Type here"
                          />
                        </td>
                      </tr>
                      <tr>
                        <td className="table-sub-title">Color</td>
                        <td>
                          <SelectInput
                            name="lense.right.coating"
                            label=""
                            className="prescription-input"
                            values={[
                              { label: "Select Coating", value: "" },
                              ...Object.values(enums.LENSE_COATINGS).map(
                                (c) => ({
                                  label: c,
                                  value: c,
                                }),
                              ),
                            ]}
                          />
                        </td>
                      </tr>
                      <tr>
                        <td className="table-sub-title">SEG</td>
                        <td>
                          <TextInput
                            className="prescription-input"
                            name="lense.right.seg"
                            placeholder="24 x"
                          />
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <div className="separator"></div>

            <div className="left-lens">
              <h3>Left Lens</h3>
              <div className="lens-table">
                <div className="table-container">
                  <table>
                    <tbody>
                      <tr>
                        <td rowSpan={5} className="table-title">
                          <p>LENS</p>
                        </td>
                      </tr>
                      <tr>
                        <td className="table-sub-title">Type</td>
                        <td>
                          <SelectInput
                            name="lense.left.type"
                            label=""
                            className="prescription-input"
                            values={[
                              { label: "Select Lense Type", value: "" },
                              ...Object.values(enums.LENSE_TYPES).map((t) => ({
                                label: t,
                                value: t,
                              })),
                            ]}
                          />
                        </td>
                      </tr>
                      <tr>
                        <td className="table-sub-title">Diameter</td>
                        <td>
                          <TextInput
                            className="prescription-input"
                            name="lense.left.diameter"
                            placeholder="Type here"
                          />
                        </td>
                      </tr>

                      <tr>
                        <td className="table-sub-title">Color</td>
                        <td>
                          <SelectInput
                            name="lense.left.coating"
                            label=""
                            className="prescription-input"
                            values={[
                              { label: "Select Coating", value: "" },
                              ...Object.values(enums.LENSE_COATINGS).map(
                                (c) => ({
                                  label: c,
                                  value: c,
                                }),
                              ),
                            ]}
                          />
                        </td>
                      </tr>
                      <tr>
                        <td className="table-sub-title">SEG</td>
                        <td>
                          <TextInput
                            className="prescription-input"
                            name="lense.left.seg"
                            placeholder="24 x"
                          />
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </Section>
          <AutoUpdateFormData onUpdate={handleGeneralUpdate} />
        </Form>
      </Formik>

      <Formik
        initialValues={{ frame: formData.frame }}
        enableReinitialize
        onSubmit={() => {}}
      >
        <Form>
          <Section title="Frame Details" className="frame-details-section">
            <div className="frame-table">
              <div className="table-container">
                <table>
                  <tbody>
                    <tr>
                      <td rowSpan={8} className="table-title">
                        <p>FRAME</p>
                      </td>
                      <td className="table-sub-title">Brand</td>
                      <td colSpan={2}>
                        <TextInput
                          className="prescription-input"
                          name="frame.brand"
                          placeholder="Type here"
                        />
                      </td>
                    </tr>
                    <tr>
                      <td className="table-sub-title">Modal</td>
                      <td colSpan={2}>
                        <TextInput
                          className="prescription-input"
                          name="frame.model_number"
                          placeholder="Type here"
                        />
                      </td>
                    </tr>
                    <tr>
                      <td rowSpan={3} className="table-sub-title">
                        Size
                      </td>
                      <td className="table-sub-sub-title">Lens diagonal</td>
                      <td>
                        <TextInput
                          className="prescription-input"
                          name="frame.lens_diagonal"
                          placeholder="Type here"
                        />
                      </td>
                    </tr>
                    <tr>
                      <td className="table-sub-sub-title">Bridge size</td>
                      <td>
                        <TextInput
                          className="prescription-input"
                          name="frame.bridge"
                          placeholder="Type here"
                        />
                      </td>
                    </tr>
                    <tr>
                      <td className="table-sub-sub-title">Arm Length</td>
                      <td>
                        <TextInput
                          className="prescription-input"
                          name="frame.arm_length"
                          placeholder="Type here"
                        />
                      </td>
                    </tr>
                    <tr>
                      <td className="table-sub-title">Color</td>
                      <td colSpan={2}>
                        <TextInput
                          className="prescription-input"
                          name="frame.color"
                          placeholder="Type here"
                        />
                      </td>
                    </tr>
                    <tr>
                      <td className="table-sub-title">P.D</td>
                      <td colSpan={2}>
                        <TextInput
                          name="frame.pd"
                          placeholder="mm"
                          type="number"
                          className="prescription-input"
                        />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </Section>
          <AutoUpdateFormData onUpdate={handleGeneralUpdate} />
        </Form>
      </Formik>

      <Section title="Charges" className="charges-section">
        <Formik
          initialValues={{
            framePrice: formData.frame.sellingPrice,
            lensePrice: formData.lense.sellingPrice,
          }}
          enableReinitialize
          onSubmit={() => {}}
        >
          <Form className="full-bx">
            <div className="left-bx full-bx">
              <div className="charges-table">
                <div className="table-container">
                  <table>
                    <tbody>
                      <tr>
                        <td className="table-sub-title">Frame Price</td>
                        <td>
                          <TextInput
                            name="framePrice"
                            type="number"
                            className="prescription-input"
                            placeholder="0.00"
                          />
                        </td>
                      </tr>
                      <tr>
                        <td className="table-sub-title">Lens Price</td>
                        <td>
                          <TextInput
                            name="lensePrice"
                            type="number"
                            className="prescription-input"
                            placeholder="0.00"
                          />
                        </td>
                      </tr>
                      <tr>
                        <td className="table-sub-title">Total</td>
                        <td>
                          {currencyFormatter(
                            parseInt(formData.frame.sellingPrice || 0) +
                              parseInt(formData.lense.sellingPrice || 0) * 2,
                          )}{" "}
                          LKR
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            <AutoUpdateFormData onUpdate={handlePriceUpdate} />
          </Form>
        </Formik>
      </Section>

      <Button
        type="button"
        text="Add new Old Prescription Form"
        icon={<DownloadIcon />}
        onClick={handleSubmit}
        disabled={createArchivedOrderFormMutation.isPending}
      />
    </div>
  );
}

function AutoUpdateFormData({ onUpdate }) {
  const { values } = useFormikContext();
  const prevValuesRef = useRef();

  useEffect(() => {
    if (!lodash.isEqual(prevValuesRef.current, values)) {
      onUpdate(values);
      prevValuesRef.current = lodash.cloneDeep(values);
    }
  }, [values, onUpdate]);
}

export default OldPrescriptionFormPage;
